/**
 * Dashboard pagination helpers — fetch the FULL relevant set, never a single
 * capped page.
 *
 * WHY: dashboard metrics are computed client-side by summing/scanning the rows a
 * query returns. A single page silently truncates the math:
 *   - PostgREST (`supabase.from().select()`) returns at most ~1000 rows by default.
 *   - pg_graphql (`xCollection(first: N)`) caps each page (and defaults to ~30
 *     rows when `first` is omitted), so an aggregate over the result is wrong the
 *     moment the real set exceeds the page.
 *
 * The fix is to page to completion (loop until a short page / no next page), NOT
 * to bump the cap to a bigger magic number. Pair these with server-side filters
 * so the relevant set stays small.
 */

import type { ApolloClient } from "@apollo/client";
import type { DocumentNode } from "graphql";
import type { TypedDocumentNode } from "@apollo/client";

/** PostgREST page size. Supabase caps a single response near this; we loop past it. */
export const SUPABASE_PAGE_SIZE = 1000;

/** pg_graphql page size we request per round-trip (the server may return fewer; we loop). */
export const GRAPHQL_PAGE_SIZE = 200;

/** Hard safety ceiling on total pages, so a bad cursor can never infinite-loop. */
const MAX_PAGES = 1000;

interface SupabasePage<T> {
  data: T[] | null;
  error: { message: string } | null;
}

/**
 * Fetch every row of a PostgREST query by looping `.range()` until a short page.
 * `buildPage` MUST construct the query FRESH each call (the Supabase builder is
 * single-use) and apply the given range, e.g.:
 *
 *   await fetchAllSupabaseRows<Deal>(({ from, to }) =>
 *     supabase.from("deals").select("*").gte("close_date", x).range(from, to)
 *   );
 */
export async function fetchAllSupabaseRows<T>(
  buildPage: (range: { from: number; to: number }) => PromiseLike<SupabasePage<T>>,
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const to = from + SUPABASE_PAGE_SIZE - 1;
    const { data, error } = await buildPage({ from, to });
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < SUPABASE_PAGE_SIZE) return all; // short page → done
    from += SUPABASE_PAGE_SIZE;
  }

  return all;
}

interface GraphqlConnection<TNode> {
  edges: ReadonlyArray<{ node: TNode }>;
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
}

/**
 * Fetch every node of a pg_graphql collection by following the `after` cursor
 * until `hasNextPage` is false. The query MUST accept `$first: Int` + `$after:
 * Cursor` and select `pageInfo { hasNextPage endCursor }`. `pick` returns the
 * connection from the query data.
 *
 * Uses `no-cache` so each page is a fresh network read (no stale merge) and the
 * full set is recomputed on demand.
 */
export async function fetchAllGraphqlNodes<TData, TNode>(
  client: ApolloClient,
  query: DocumentNode | TypedDocumentNode<TData, Record<string, unknown>>,
  variables: Record<string, unknown>,
  pick: (data: TData) => GraphqlConnection<TNode> | null | undefined,
): Promise<TNode[]> {
  const all: TNode[] = [];
  let after: string | null = null;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result = await client.query<TData>({
      query,
      variables: { ...variables, first: GRAPHQL_PAGE_SIZE, after },
      fetchPolicy: "no-cache",
    });
    if (!result.data) return all;
    const connection = pick(result.data);
    const edges = connection?.edges ?? [];
    for (const edge of edges) all.push(edge.node);

    const pageInfo = connection?.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return all;
    after = pageInfo.endCursor;
  }

  return all;
}
